import CustomElementPrepareModelV3 from './custom-element-prepare-model';
import ImagePrepareModel from './image-prepare-model';
import DefaultPrepareModel from './default-prepare-model';
import TablePrepareModel from './table-prepare-model';
import type { ISdkModel, ITextBodyModel } from '../../store-manager';
import type { ImageBlobAndString } from '../image-prepare-plugin';
import { ICopyPasteInjectable, CopyPastePrivateTag } from '../../../inject/copy-paste-injectable.interface';

export enum HTMLType {
    TABLE,
    IMAGE,
    CUSTOM,
    CUSTOM_INHERIT_AND_NO_INHERIT,
    UNFIT_ELEMENT,
    MULTI_ELEMENT,
    SLIDE,
}

export interface HTMLOutputOptions {
    useAsync?: boolean;
}

export interface ElementHTML {
    type: Exclude<HTMLType, HTMLType.CUSTOM_INHERIT_AND_NO_INHERIT>;
    data: ISdkModel | Promise<ISdkModel>;
    outputOptions: HTMLOutputOptions
}

export interface ElementInheritHTML {
    type: HTMLType.CUSTOM_INHERIT_AND_NO_INHERIT;
    data: ITextBodyModel;
    outputOptions: HTMLOutputOptions
}

class HTMLPreparePlugin {
    private customElementPrepareModel: CustomElementPrepareModelV3;
    private imagePrepareModel: ImagePrepareModel;
    private defaultPrepareModel: DefaultPrepareModel;
    private tablePrepareModel: TablePrepareModel;
    public constructor(private copyPasteInjectable: ICopyPasteInjectable) {
        this.customElementPrepareModel = new CustomElementPrepareModelV3(copyPasteInjectable);
        this.imagePrepareModel = new ImagePrepareModel(copyPasteInjectable);
        this.defaultPrepareModel = new DefaultPrepareModel(copyPasteInjectable);
        this.tablePrepareModel = new TablePrepareModel(copyPasteInjectable);
    }

    /**
     * 数据Model转html入口，分为几大类型
     * @param htmlConf html数据
     * @returns
     */
    public getHTMLString(
        htmlConf: ElementHTML | ElementInheritHTML,
        imageBlobAndString: ImageBlobAndString,
        forceSync: boolean,
        base64UrlTempMap?: Map<string, string>|undefined,
    ): Promise<string> | string {
        const useAsync = htmlConf.outputOptions?.useAsync && !forceSync;
        return useAsync
            ? this.asyncGetHTMLString(htmlConf, imageBlobAndString, base64UrlTempMap)
            : this.syncGetHTMLString(htmlConf, imageBlobAndString, base64UrlTempMap);
    }

    public async getHTMLBlob(
        htmlData: ElementHTML | ElementInheritHTML,
        imageBlobAndString: ImageBlobAndString,
        base64UrlTempMap?: Map<string, string>|undefined,
    ): Promise<Blob> {
        const blobProperty = { type: 'text/html' };
        const blobValue = await this.getHTMLString(htmlData, imageBlobAndString, false, base64UrlTempMap);
        return new Blob([blobValue], blobProperty);
    }

    public async HTMLblobToText(blobData: Blob): Promise<{
        html: string;
        privateData: string | null | undefined;
        privateTag: CopyPastePrivateTag
    }> {
        const html = await blobData.text();
        const { privateData, privateTag } = this.defaultPrepareModel.getPrivateData(html);
        return { html, privateData, privateTag };
    }

    public HTMLStringToText(htmlString = ''): { html: string; privateData: string | null | undefined; privateTag: CopyPastePrivateTag } {
        const { privateData, privateTag } = this.defaultPrepareModel.getPrivateData(htmlString);
        return { html: htmlString, privateData, privateTag };
    }

    /**
     * 同步数据Model转html入口，分为几大类型
     * @param htmlConf {ElementHTML | ElementInheritHTML}
     * @param imageBlobAndString {ImageBlobAndString} 使用截图工具生成的图片数据
     * @returns
     */
    private syncGetHTMLString(
        htmlConf,
        imageBlobAndString: ImageBlobAndString,
        base64UrlTempMap: Map<string, string>|undefined,
    ) {
        if (htmlConf.type === HTMLType.TABLE) {
            return this.tablePrepareModel.getHtml(htmlConf.data);
        }
        if (htmlConf.type === HTMLType.IMAGE) {
            this.imagePrepareModel.base64UrlTempMap = base64UrlTempMap;
            return this.imagePrepareModel.getHtml(htmlConf.data);
        }
        if (htmlConf.type === HTMLType.CUSTOM) {
            return this.customElementPrepareModel.getHtml(htmlConf.data, imageBlobAndString);
        }
        if (htmlConf.type === HTMLType.CUSTOM_INHERIT_AND_NO_INHERIT) {
            return this.customElementPrepareModel.getHtml(htmlConf.data, imageBlobAndString);
        }
        return this.defaultPrepareModel.getHtml(htmlConf.data, imageBlobAndString);
    }

    /**
     * 异步数据Model转html入口，分为几大类型
     * @param htmlConf {ElementHTML | ElementInheritHTML}
     * @param imageBlobAndString {ImageBlobAndString} 使用截图工具生成的图片数据
     * @returns
     */
    private asyncGetHTMLString(
        htmlConf,
        imageBlobAndString: ImageBlobAndString,
        base64UrlTempMap: Map<string, string>|undefined,
    ) {
        if (htmlConf.type === HTMLType.TABLE) {
            return this.tablePrepareModel.asyncGetHtml(htmlConf.data);
        }
        if (htmlConf.type === HTMLType.IMAGE) {
            this.imagePrepareModel.base64UrlTempMap = base64UrlTempMap;
            return this.imagePrepareModel.asyncGetHtml(htmlConf.data);
        }
        if (htmlConf.type === HTMLType.CUSTOM) {
            return this.customElementPrepareModel.asyncGetHtml(htmlConf.data, imageBlobAndString);
        }
        if (htmlConf.type === HTMLType.CUSTOM_INHERIT_AND_NO_INHERIT) {
            return this.customElementPrepareModel.asyncGetHtml(htmlConf.data, imageBlobAndString);
        }
        return this.defaultPrepareModel.asyncGetHtml(htmlConf.data, imageBlobAndString);
    }
}

export default HTMLPreparePlugin;
